<template>
  <v-container fluid>
    <!-- Aviso de rango mínimo y máximo de la matriz -  Subórdenes -->
    <v-row v-if="subOrder && matrix && !isOneRow && outOfRange" class="my-0">
      <v-col cols="12">
        <v-alert type="error" outlined color="error">
          <h4>{{ $t('out_of_range') }}</h4>
          <ul>
            <li>min: {{ valuesMatrix[0] }}</li>
            <li>max: {{ valuesMatrix[1] }}</li>
          </ul>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Formulario de selección de datos de la matriz - Subórdenes -->
    <v-row v-if="subOrder" class="my-0">
      <!-- Costo -->
      <v-col v-if="canViewPrice" cols="12" class="pt-0">
        <p class="font-weight-bold body-2">
          <span v-show="!canViewCost">{{ $t('f2key') }}</span>
          <i-money
            v-show="canViewCost"
            class="font-weight-black primary--text"
            mode="text"
            :value="valuesMatrix[3]"
            :label="`${$tc('cost', 1)} = `"
          />
        </p>
      </v-col>

      <!-- Cantidad / Filas -->
      <v-col cols="6" md="4" lg="3">
        <ValidationProvider
          v-if="isOneRow"
          vid="quantity"
          :name="$tc('amount', 1)"
          rules="required"
          v-slot="{ errors }"
        >
          <v-select
            color="primary"
            outlined
            :disabled="
              subOrder.orderMain !== 'draft' && subOrder.orderMain !== 'quote'
            "
            :error-messages="errors[0]"
            :items="row_list"
            :label="$tc('amount', 1)"
            @change="postString"
            v-model="quantity"
          ></v-select>
        </ValidationProvider>
        <ValidationProvider
          v-else
          vid="amount"
          :name="$tc('amount', 1)"
          :rules="{
            required: true,
            double: 2,
            min_value: valuesMatrix[0] === undefined ? false : valuesMatrix[0],
            max_value:
              valuesMatrix[1] === undefined || valuesMatrix[1] === 0
                ? false
                : valuesMatrix[1]
          }"
          v-slot="{ errors }"
        >
          <v-text-field
            color="secondary"
            outlined
            type="number"
            :disabled="
              subOrder.orderMain !== 'draft' && subOrder.orderMain !== 'quote'
            "
            :error-messages="errors[0]"
            :label="$tc('amount', 1)"
            @change="postString"
            v-model="quantity"
          />
        </ValidationProvider>
      </v-col>

      <!-- Tabs -->
      <v-col v-if="sides.length > 0" cols="6" md="4" lg="3">
        <ValidationProvider
          vid="side"
          :name="$tc(tab_name, 1)"
          rules="required"
          v-slot="{ errors }"
        >
          <v-select
            color="primary"
            outlined
            :disabled="
              subOrder.orderMain !== 'draft' && subOrder.orderMain !== 'quote'
            "
            :error-messages="errors[0]"
            :items="sides"
            :label="$tc(tab_name, 1)"
            @change="postString"
            v-model="side"
          ></v-select>
        </ValidationProvider>
      </v-col>

      <!-- Columnas -->
      <v-col cols="6" md="4" lg="3">
        <ValidationProvider
          vid="col"
          :name="$tc(column_name, 1)"
          rules="required"
          v-slot="{ errors }"
        >
          <v-select
            color="primary"
            outlined
            :disabled="
              subOrder.orderMain !== 'draft' && subOrder.orderMain !== 'quote'
            "
            :error-messages="errors[0]"
            :items="columns"
            :label="$tc(column_name, 1)"
            @change="postString"
            v-model="column"
          ></v-select>
        </ValidationProvider>
      </v-col>

      <!-- Precio -->
      <v-col v-if="canViewPrice" cols="6" md="4" lg="3">
        <i-money
          disabled
          mode="input"
          :label="`${$t('sale_price')}:`"
          :value="valuesMatrix[2]"
        />
      </v-col>

      <!-- Subtotal -->
      <v-col
        v-if="!hideSubtotal"
        cols="12"
        md="4"
        lg="3"
        offset="0"
        :offset-md="sides.length > 0 ? 4 : 8"
        :offset-lg="sides.length > 0 ? 9 : 0"
      >
        <v-alert type="info" outlined color="primary">
          <i-money
            :value="valuesMatrix[2] * qty"
            mode="text"
            :label="$t('subtotal')"
          />
        </v-alert>
      </v-col>
    </v-row>

    <!-- Lista de matrices con costos y precios por cada tipo de cliente -->
    <v-row v-if="showMatrix" class="my-0">
      <v-col v-if="hasMatrix || showLevels" cols="11">
        <v-select
          v-if="checkPermissions.matrixTemplate && checkPermissions.level"
          autocomplete="off"
          color="secondary"
          hide-details
          key="level"
          name="level"
          outlined
          :items="levels"
          item-text="name"
          item-value="pk"
          :label="$tc('level', 1)"
          @change="setOtherMatrix"
          v-model="level"
        />
        <br v-if="level === null" />
      </v-col>

      <!-- Lista de plantillas de matriz -->
      <v-col cols="11" v-else>
        <v-select
          v-if="
            checkPermissions.matrixTemplate &&
            new_template == null &&
            main_matrix == null
          "
          autocomplete="off"
          color="secondary"
          hide-details
          key="matrix"
          name="matrix"
          outlined
          :clearable="matrixs.length > 10"
          :items="matrixs"
          item-text="name"
          item-value="pk"
          :label="$tc('matrix', 1)"
          @change="setTemplate"
          :append-icon="matrixs.length > 10 ? 'fa-search' : ''"
          v-model="matrix"
        />
        <br v-if="matrix === null" />
      </v-col>

      <!-- Botón de guardado -->
      <v-col class="align-self-center pt-0" cols="1">
        <i-btn
          color="primary"
          icon="fa-save"
          outlined
          :title="$tc('save', 1)"
          @click="submit"
        ></i-btn>
      </v-col>

      <!-- Creación de matriz -->
      <v-col v-if="!hasMatrix || level > 0" cols="12">
        <create-matrix
          ref="product_matrix"
          :detailId="detailId"
          :levelId="level"
          :new_template="new_template"
          :productId="productId"
          :subdetailId="subdetailId"
          @hasMatrix="hasMatrix = $event"
        />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import CreateMatrix from '@/pages/matrix/form.vue'
import { mapGetters } from 'vuex'
export default {
  components: {
    CreateMatrix
  },
  props: {
    subOrder: {
      type: Object,
      default: null
    },
    detail: {
      type: [Object, Array],
      default: null
    },
    type_customer: {
      type: [Number, String]
    },
    productId: {
      type: [String, Number],
      default: null
    },
    price: {
      type: [String, Number],
      default: 0
    },
    qty: {
      type: [Number, String],
      default: 0
    },
    detailId: {
      type: [String, Number],
      default: null
    },
    subdetailId: {
      type: [String, Number],
      default: null
    },
    new_template: {
      type: Number,
      default: null
    },
    main_matrix: {
      type: Number,
      default: null
    },
    priceString: {
      type: String,
      default: ''
    },
    matrixUse: {
      type: String,
      required: true
    },
    showMatrix: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    hideSubtotal: {
      type: Boolean,
      default: false
    },
    showLevels: {
      type: Boolean,
      default: false
    },
    canViewPrice: {
      type: Boolean,
      default: true
    },
    canViewCost: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      edit: false,
      quantity: 0,
      sides: [],
      side: '',
      columns: [],
      column: '',
      column_name: '',
      tab_name: '',
      levels: [],
      level: null,
      matrixs: [],
      matrix: null,
      hasMatrix: false,
      matrixProduct: false,
      row_list: []
    }
  },
  methods: {
    /**
     * Método para asignar la cantidad de la orden de venta y llamar la funcion
     * de getMatrix
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async seeMatrix() {
      if (this.subOrder !== null) {
        this.quantity = this.subOrder.quantity
      } else {
        this.quantity = this.qty
      }
      if (this.productId !== null) {
        this.getMatrix('product')
      } else {
        this.getMatrix('process')
      }
    },
    /**
     * Método para consultar la matriz según su tipo con sus costos y precios
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getMatrix(type) {
      const params = {
        product_id: this.productId,
        level_id:
          this.type_customer === undefined ? this.level : this.type_customer,
        detail_id: this.detailId,
        subdetail_id: this.subdetailId !== '' ? this.subdetailId : null
      }
      if (type === 'product') {
        await this.$api.matrix
          .list({
            opt: {
              params: params
            }
          })
          .then((response) => {
            this.setMatrix(response)
          })
          .catch((error) => {
            console.error(error)
          })
      } else {
        await this.$api.matrix.matrixprocess
          .list({
            opt: {
              params: params
            }
          })
          .then((response) => {
            this.setMatrix(response)
          })
          .catch((error) => {
            console.error(error)
          })
      }
    },
    /**
     * Método que envia los costos y los precios de la matriz
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async setMatrix(response) {
      let matrix = response.data[0]
      if (matrix !== undefined && matrix !== null) {
        this.hasMatrix = true
        this.sides = matrix.rows_title
        this.columns = matrix.matrix_values[1].col_title
        this.column_name = matrix.matrix_values[1].item_value.value
        this.tab_name = matrix.tab_name
        if (this.$refs['product_matrix']) {
          this.$refs.product_matrix.setValues(matrix, true)
        }
        this.matrix = matrix
      }
      if (this.subOrder !== null) {
        this.matrix = matrix
      }
    },
    /**
     * Método que guardar la matriz con costos y precios, y activar el producto
     * donde fue seleccionada.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    submit() {
      if (this.$refs['product_matrix'] !== undefined) {
        this.$refs['product_matrix'].submit(true)
        this.getLevels()
        this.$emit('update:active', true)
      } else {
        this.$toast.error(`${this.$tc('required', 1)}`)
      }
    },
    /**
     * Método pasar como una fórmula los valores seleccionados de la matriz en
     * la orden de venta.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async postString() {
      if (
        this.subOrder.price_type === 'matrix' ||
        this.detail.price_type === 'matrix' ||
        this.detail.pk !== ''
      ) {
        let post = ''
        post = this.valuesMatrix[2]
        post += '*' + this.valuesMatrix[3]
        post += '*' + this.column
        if (this.side) {
          post += '*' + this.side
        }

        if (this.detail !== null && this.detail !== undefined) {
          this.detail.priceString = post
          this.detail.price = this.valuesMatrix[2]
          this.$emit('change', this.valuesMatrix[2])
        } else {
          this.subOrder.priceString = post
          this.subOrder.price = this.valuesMatrix[2]
          this.subOrder.quantity = this.quantity
          this.$emit('price', this.valuesMatrix[2])
        }
      }
    },
    /**
     * Método que consulta una plantilla de matriz
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getMatrixTemplate(pk, productId) {
      let matrix = await this.$api.matrix.template.show({ pk: pk })
      matrix = matrix.data
      if (
        matrix &&
        matrix.matrix_template_values &&
        this.$refs['product_matrix'] != undefined
      ) {
        this.$refs['product_matrix'].setTemplate(matrix, productId)
        this.getLevels()
      }
    },
    /**
     * Método que consulta los precios para cada tipo de cliente
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getLevels() {
      const level = await this.$api.customer.level.list({
        opt: {
          params: {
            company_id: this.company.pk
          }
        }
      })
      this.levels = level.data.results
    },
    /**
     * Método que consulta la matriz principal de un producto con tipo de precio
     * matriz
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getMatrixProduct() {
      const product = await this.$api.product.show({
        pk: this.productId
      })
      if (product.data.price_type === 'matrix') {
        await this.$api.matrix
          .list({
            opt: {
              params: {
                product_id: this.productId,
                level_id: null,
                detail_id: null
              }
            }
          })
          .then((response) => {
            if (response.data.length > 0) {
              this.matrixProduct = response.data[0].detail === null
            }
          })
      } else {
        this.matrixProduct = true
      }
    },
    /**
     * Método que consulta las matrices tipo item o subitem, de una plantilla de
     * proceso productivo
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getMatrixDetail() {
      await this.$api.matrix.matrixprocess
        .list({
          opt: {
            params: {
              detail_id: this.detailId,
              subdetail_id: this.subdetailId !== '' ? this.subdetailId : null,
              level_id: null,
              detail_id: null
            }
          }
        })
        .then((response) => {
          if (response.data.length > 0) {
            this.matrixProduct = response.data[0].detail === null
          }
        })
    },
    /**
     * Método que envía los parámetros de la matriz tipo detalle que se desea
     * visualizar.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    setOtherMatrix() {
      this.seeMatrix(
        this.productId,
        this.type_customer === undefined ? this.level : this.type_customer,
        this.detailId,
        this.subdetailId
      )
    },
    /**
     * Método que consulta la lista de plantillas de matriz según su uso
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getMatrixTemplates() {
      const matrix = await this.$api.matrix.template.list({
        opt: {
          params: {
            matrix_use: this.matrixUse
          }
        }
      })
      this.matrixs = matrix.data.results
      this.seeMatrix()
    },
    setTemplate() {
      this.getMatrixTemplate(
        this.main_matrix == null ? this.matrix : this.main_matrix,
        this.productId
      )
    },
    /**
     * Método que toma los datos de los rangos únicos de una matriz, elimina el
     * " -" del final, parsea las cadenas y los devuelve en forma de lista.
     * Se invoca únicamente si la matriz es de tipo onerow en la suborden de
     * venta. Esto se verifica en el computado isOneRow.
     *
     * @param {Array} rowlist   Listado de los rangos únicos de la matriz
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    setRowList(rowlist = []) {
      const parseToNumber = rowlist.map((item) => {
        const onlyNumber = item.split(' ')[0]
        return parseFloat(onlyNumber)
      })
      this.row_list = parseToNumber
    },
    /**
     * Función que parsea los datos de priceString y los ordena dentro de un
     * objeto más amigable para su uso.
     *
     * @param {String} priceString - Fórmula final de los datos de la matriz
     * @returns {Object} Valores del priceString ordenados adecuadamente
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    parseArgs(priceString) {
      const args = {
        price: 0,
        cost: 0,
        column: '',
        tab: ''
      }

      const [num1 = 0, num2 = 0, column = '', tab = ''] = priceString.split('*')
      args.cost = this.costOrPrice('cost', num1, num2)
      args.price = this.costOrPrice('price', num1, num2)
      args.column = column
      args.tab = tab

      return args
    },
    /**
     * Función para verificar cual es el costo y cual es el precio entre dos
     * valores dados.
     *
     * @param {String} [type='cost'] Valor que se quiere buscar
     * @param {Number} num1 Primero número a comparar
     * @param {Number} num2 Segundo número a comparar
     * @returns {Number}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    costOrPrice(type = 'cost', num1, num2) {
      const float1 = parseFloat(num1)
      const float2 = parseFloat(num2)
      let result = 0
      if (type === 'cost') {
        result = Math.min(float1, float2)
      } else {
        result = Math.max(float1, float2)
      }
      return result
    }
  },
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      company: 'company/getCompanyData',
      isAdmin: 'session/isAdmin'
    }),
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @returns {Object} Objeto con los permisos del usuario.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        matrixTemplate: this.getPermissions([`matrix.view_matrixtemplate`]),
        level: this.getPermissions([`base_config.view_level`])
      }
    },
    /**
     * Computado que calcula los rango mínimo y máximo de una matriz y también
     * guarda los valores de precio y costo de ese rango.
     *
     * @returns {Array} [Mínimo, máximo, precio, costo]
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    valuesMatrix() {
      if (this.matrix && this.column) {
        const rows = this.matrix.matrix_values[1].rows_title
        const inxCol = this.columns.findIndex((item) => item === this.column)

        let position = []
        rows.forEach((range, index) => {
          let selectRange =
            range.split('-')[1] > 0 ? range.split('-')[1] : range.split('-')[0]
          if (this.quantity <= Number(selectRange) && position.length === 0) {
            position[0] = index
          }
        })

        let inxSide = 0
        if (this.sides.length > 0 || this.side) {
          inxSide = this.sides.findIndex((item) => item === this.side)
        }

        let values = []
        values[0] = Number(rows[0].split('-')[0])
        values[1] =
          Number(rows[rows.length - 1].split('-')[1]) > 0
            ? Number(rows[rows.length - 1].split('-')[1])
            : Number(rows[rows.length - 1].split('-')[0])

        if (position[0] === undefined) {
          return [values[0], values[1], -1, -1]
        }
        if (inxCol > -1) {
          values[2] = this.costOrPrice(
            'price',
            this.matrix.matrix_values[inxSide * 2 + 1].values[position[0]][
              inxCol
            ],
            this.matrix.matrix_values[inxSide * 2].values[position[0]][inxCol]
          )
          values[3] = this.costOrPrice(
            'cost',
            this.matrix.matrix_values[inxSide * 2 + 1].values[position[0]][
              inxCol
            ],
            this.matrix.matrix_values[inxSide * 2].values[position[0]][inxCol]
          )
        }
        return values
      }
      return [0, 0, 0, 0]
    },
    /**
     * Computado que revisa si la matriz es de una sola fila o doble
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    isOneRow() {
      if (this.matrix?.matrix_values && this.matrix.matrix_values.length) {
        let firstRow = this.matrix.matrix_values[0].rows_title[0]
        if (firstRow?.endsWith('-')) {
          this.setRowList(this.matrix.matrix_values[0].rows_title)
          return true
        }
      }
      return false
    },
    /**
     * Computado que revisa la cantidad está fuera de los rangos de la matriz
     * @returns {Boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    outOfRange() {
      let min = Number(this.valuesMatrix[0])
      let max = Number(this.valuesMatrix[1])
      let qty = Number(this.quantity)
      if (qty < min || qty > max) {
        return true
      }
      return false
    }
  },
  mounted() {
    if (this.matrix === null) {
      this.getMatrixTemplates()
      this.seeMatrix()
      this.getLevels()
    }
    if (this.productId !== null) {
      this.getMatrixProduct()
      if (this.main_matrix != null) {
        this.setTemplate(this.main_matrix, this.productId)
      }
    } else {
      this.getMatrixDetail()
    }
    if (this.new_template != null) {
      this.getMatrixTemplate(this.new_template, this.productId)
    }
    if (this.matrix != null) {
      this.getMatrixTemplate(this.matrix.pk, this.productId)
    }
    if (this.priceString) {
      this.column = this.parseArgs(this.priceString)['column']
      this.side = this.parseArgs(this.priceString)['tab']
    }
  }
}
</script>
